import { AirbnbOfficialLinkStatus } from 'uplisting-frontend/models/airbnb-official-link';
import type { BookingDotComLinkStatus } from 'uplisting-frontend/models/booking-dot-com-link';
import type { ExternalIcalSyncStatus } from 'uplisting-frontend/models/external-ical';
import type { TagTone } from 'uplisting-frontend/components/ui/tag';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { bdcExtranetUrl } from 'uplisting-frontend/utils/bdc-constants';

export enum ChannelStatus {
  connected = 'connected',
  readOnly = 'read_only',
  settingUp = 'setting_up',
  draft = 'draft',
  needsAttention = 'needs_attention',
  notConnected = 'not_connected',
}

export interface IChannelStatusReading {
  status: ChannelStatus;
  // Key under ui_channel_status.reasons.
  reason?: string;
  // A listing that reads its account's status, which the account card already shows.
  fromAccount?: true;
}

export interface ILinkedProperty {
  id: string | null;
  name: string;
}

export interface IListingActions {
  // The saved link Unlink acts on; absent when the API sends no link id.
  link?: ModelRegistry['channel-link'];
  fixUrl?: string;
  isUnlinking?: boolean;
}

const TONES: Record<ChannelStatus, TagTone> = {
  [ChannelStatus.connected]: 'positive',
  [ChannelStatus.readOnly]: 'neutral',
  [ChannelStatus.settingUp]: 'info',
  [ChannelStatus.draft]: 'info',
  [ChannelStatus.needsAttention]: 'moderate',
  [ChannelStatus.notConnected]: 'neutral',
};

// Lowest first: what needs the user sorts to the top.
const RANKS: Record<ChannelStatus, number> = {
  [ChannelStatus.needsAttention]: 0,
  [ChannelStatus.notConnected]: 0,
  [ChannelStatus.readOnly]: 1,
  [ChannelStatus.settingUp]: 2,
  [ChannelStatus.draft]: 2,
  [ChannelStatus.connected]: 3,
};

const reading = (
  status: ChannelStatus,
  reason?: string | null,
): IChannelStatusReading => (reason ? { status, reason } : { status });

// An account's reason, as worded on each of its linked listings.
const LISTING_REASONS: Record<string, string> = {
  airbnb_read_only: 'listing_read_only',
  airbnb_access_lost: 'listing_account_access_lost',
  pending_contract: 'listing_pending_contract',
};

const BOOKING_DOT_COM_LINK: Record<
  BookingDotComLinkStatus,
  IChannelStatusReading
> = {
  capturing_hotel_id: reading(ChannelStatus.notConnected),
  captured_hotel_id: reading(ChannelStatus.settingUp, 'bdc.captured_hotel_id'),
  awaiting_confirmation: reading(
    ChannelStatus.settingUp,
    'bdc.awaiting_confirmation',
  ),
  connection_confirmed: reading(
    ChannelStatus.settingUp,
    'bdc.connection_confirmed',
  ),
  room_matched: reading(ChannelStatus.settingUp, 'bdc.room_matched'),
  all_rooms_matched: reading(ChannelStatus.settingUp, 'bdc.all_rooms_matched'),
  draft: reading(ChannelStatus.draft, 'publishing_not_finished'),
  connection_locked: reading(ChannelStatus.connected),
  room_not_active: reading(ChannelStatus.needsAttention, 'room_not_active'),
  rate_not_syncable: reading(ChannelStatus.needsAttention, 'rate_not_syncable'),
};

const AIRBNB_OFFICIAL_LINK: Record<
  AirbnbOfficialLinkStatus,
  IChannelStatusReading
> = {
  [AirbnbOfficialLinkStatus.confirmed]: reading(ChannelStatus.connected),
  [AirbnbOfficialLinkStatus.readOnly]: reading(
    ChannelStatus.readOnly,
    'airbnb_read_only',
  ),
  [AirbnbOfficialLinkStatus.onboarding]: reading(
    ChannelStatus.settingUp,
    'onboarding.airbnb_listing',
  ),
  [AirbnbOfficialLinkStatus.inactive]: reading(
    ChannelStatus.needsAttention,
    'airbnb_listing_inactive',
  ),
};

const EXTERNAL_ICAL: Record<ExternalIcalSyncStatus, IChannelStatusReading> = {
  successful: reading(ChannelStatus.connected),
  pending: reading(ChannelStatus.settingUp, 'ical.pending'),
  failing: reading(ChannelStatus.needsAttention, 'ical.failing'),
  failed: reading(ChannelStatus.needsAttention, 'ical.failed'),
};

export function channelStatusTone(status: ChannelStatus): TagTone {
  return TONES[status];
}

export function channelStatusRank(status: ChannelStatus): number {
  return RANKS[status];
}

export function connectionStatus(
  connection: ModelRegistry['channel-connection'],
): IChannelStatusReading {
  switch (connection.status) {
    case 'onboarding':
      return reading(ChannelStatus.settingUp, `onboarding.${connection.type}`);
    case 'pending_contract':
      return reading(ChannelStatus.needsAttention, 'pending_contract');
    case 'inactive':
      return reading(ChannelStatus.needsAttention, 'airbnb_access_lost');
    case 'pending':
    case 'syncing':
      return reading(ChannelStatus.settingUp);
    case 'mapping':
    case 'failed':
      return reading(ChannelStatus.needsAttention);
    case 'active':
    case null:
      return connection.isReadOnly
        ? reading(ChannelStatus.readOnly, 'airbnb_read_only')
        : reading(ChannelStatus.connected);
  }
}

const draftPublication = (
  listing: ModelRegistry['channel-listing'],
  connection?: ModelRegistry['channel-connection'],
): ModelRegistry['booking-dot-com-hotel-publication'] | undefined => {
  const publication = listing.bookingDotComHotelPublication;

  return publication?.status === 'draft' ||
    (publication && connection?.isPendingContract)
    ? publication
    : undefined;
};

const savedLink = (
  listing: ModelRegistry['channel-listing'],
  connection?: ModelRegistry['channel-connection'],
): ModelRegistry['channel-link'] | undefined =>
  connection?.channelLinks.find(
    (item) => item.externalId === listing.externalId && !item.isNew,
  );

export function listingStatus(
  listing: ModelRegistry['channel-listing'],
  connection?: ModelRegistry['channel-connection'],
): IChannelStatusReading {
  if (draftPublication(listing, connection)) {
    return reading(ChannelStatus.draft, 'publishing_not_finished');
  }

  const link = savedLink(listing, connection);

  if (!connection || !link) {
    return reading(ChannelStatus.notConnected, 'no_property_linked');
  }

  if (link.needsAttention) {
    return reading(ChannelStatus.needsAttention, link.status);
  }

  const account = connectionStatus(connection);

  if (account.status === ChannelStatus.connected) {
    return account;
  }

  return {
    ...reading(account.status, LISTING_REASONS[account.reason ?? '']),
    fromAccount: true,
  };
}

export function listingActions(
  listing: ModelRegistry['channel-listing'],
  connection?: ModelRegistry['channel-connection'],
): IListingActions {
  const link = savedLink(listing, connection);

  if (!link || draftPublication(listing, connection)) {
    return {};
  }

  return {
    link: link.linkId ? link : undefined,
    fixUrl:
      link.needsAttention && listing.hotelId
        ? bdcExtranetUrl(listing.hotelId)
        : undefined,
  };
}

export function linkedProperty(
  listing: ModelRegistry['channel-listing'],
  properties: ModelRegistry['property'][],
): ILinkedProperty {
  const connection = listing.channelConnection;
  const draft = draftPublication(listing, connection);

  if (draft) {
    return {
      id: draft.property?.id ?? null,
      name: draft.property?.nickname ?? '',
    };
  }

  // Unlike the status, this includes a property picked but not yet imported.
  const link = connection?.channelLinks.find(
    (item) => item.externalId === listing.externalId,
  );

  return {
    id: link?.isNew ? null : (link?.propertyId ?? null),
    name:
      properties.find((property) => property.id === link?.propertyId)
        ?.nickname ?? '',
  };
}

export function bookingDotComLinkStatus(
  link?: ModelRegistry['booking-dot-com-link'],
): IChannelStatusReading {
  return link
    ? BOOKING_DOT_COM_LINK[link.status]
    : reading(ChannelStatus.notConnected);
}

export function airbnbOfficialLinkStatus(
  link?: ModelRegistry['airbnb-official-link'],
): IChannelStatusReading {
  return link?.status
    ? AIRBNB_OFFICIAL_LINK[link.status]
    : reading(ChannelStatus.notConnected);
}

export function googleAriLinkStatus(
  link?: ModelRegistry['google-ari-link'],
): IChannelStatusReading {
  return reading(
    link?.listable ? ChannelStatus.connected : ChannelStatus.notConnected,
  );
}

export function externalIcalStatus(
  ical?: ModelRegistry['external-ical'],
): IChannelStatusReading {
  return ical && !ical.isNew
    ? EXTERNAL_ICAL[ical.syncStatus]
    : reading(ChannelStatus.notConnected);
}
